import { useState, useEffect } from 'react';
import axios from 'axios';
import RRHHLayout from '@/components/RRHHLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  FileText, 
  AlertTriangle,
  UserX,
  DollarSign,
  Plane,
  Award,
  FileCheck2,
  ChevronRight,
  ChevronLeft,
  Download,
  CheckCircle2,
  Eye,
  Search,
  Filter
} from 'lucide-react';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIPOS_DOCUMENTOS = [
  { 
    id: 'contrato', 
    nombre: 'Contrato de Trabajo', 
    icon: FileText,
    descripcion: 'Contrato individual de trabajo según código laboral'
  },
  { 
    id: 'amonestacion', 
    nombre: 'Carta de Amonestación', 
    icon: AlertTriangle,
    descripcion: 'Amonestación escrita por falta o incumplimiento'
  },
  { 
    id: 'despido', 
    nombre: 'Carta de Despido', 
    icon: UserX,
    descripcion: 'Notificación de terminación de contrato con causa justa'
  },
  { 
    id: 'finiquito', 
    nombre: 'Finiquito Laboral', 
    icon: DollarSign,
    descripcion: 'Liquidación final y pago de prestaciones'
  },
  { 
    id: 'vacaciones', 
    nombre: 'Carta de Salida de Vacaciones', 
    icon: Plane,
    descripcion: 'Autorización de periodo vacacional'
  },
  { 
    id: 'recomendacion', 
    nombre: 'Carta de Recomendación', 
    icon: Award,
    descripcion: 'Referencia laboral del trabajador'
  },
  { 
    id: 'constancia_salarial', 
    nombre: 'Constancia Salarial', 
    icon: FileCheck2,
    descripcion: 'Certificación de ingresos del trabajador'
  }
];

export default function HistorialDisciplinario({ user, onLogout }) {
  
  // Estados principales
  const [paso, setPaso] = useState(0);
  const [tipoDocumento, setTipoDocumento] = useState(null);
  const [trabajadorSeleccionado, setTrabajadorSeleccionado] = useState(null);
  const [trabajadores, setTrabajadores] = useState([]);
  const [loadingTrabajadores, setLoadingTrabajadores] = useState(false);
  
  // Estados del formulario
  const [formData, setFormData] = useState({});
  const [previewDialog, setPreviewDialog] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Estados de la tabla
  const [documentosGenerados, setDocumentosGenerados] = useState([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroTrabajador, setFiltroTrabajador] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  
  // Estado para previsualización de documentos en la tabla
  const [documentoPreview, setDocumentoPreview] = useState(null);
  const [previewTableDialog, setPreviewTableDialog] = useState(false);

  useEffect(() => {
    loadTrabajadores();
    loadDocumentosGenerados();
  }, []);

  const loadTrabajadores = async () => {
    setLoadingTrabajadores(true);
    try {
      const response = await axios.get(`${API}/trabajadores`);
      setTrabajadores(response.data);
    } catch (error) {
      toast.error('Error al cargar trabajadores');
    } finally {
      setLoadingTrabajadores(false);
    }
  };

  const loadDocumentosGenerados = async () => {
    setLoadingDocumentos(true);
    try {
      const response = await axios.get(`${API}/documentos/generados`);
      setDocumentosGenerados(response.data);
    } catch (error) {
      toast.error('Error al cargar documentos');
    } finally {
      setLoadingDocumentos(false);
    }
  };

  const handleSelectTipoDocumento = (tipo) => {
    setTipoDocumento(tipo);
    setPaso(1);
  };

  const handleSelectTrabajador = (trabajadorId) => {
    const trabajador = trabajadores.find(t => t.id === trabajadorId);
    setTrabajadorSeleccionado(trabajador);
    setPaso(2);
  };

  const resetearFormulario = () => {
    setPaso(0);
    setTipoDocumento(null);
    setTrabajadorSeleccionado(null);
    setFormData({});
    setPdfUrl(null);
  };

  const getDocumentosFiltrados = () => {
    let filtered = documentosGenerados;

    // Filtro por tipo
    if (filtroTipo !== 'todos') {
      filtered = filtered.filter(doc => doc.tipo_documento === filtroTipo);
    }

    // Filtro por trabajador
    if (filtroTrabajador !== 'todos') {
      filtered = filtered.filter(doc => doc.trabajador_id === filtroTrabajador);
    }

    // Filtro por búsqueda (nombre trabajador, código, cédula)
    if (busqueda) {
      const searchLower = busqueda.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.trabajador_nombre.toLowerCase().includes(searchLower) ||
        doc.codigo.toLowerCase().includes(searchLower) ||
        doc.trabajador_cedula.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  };

  const getTotalPasos = () => {
    switch(tipoDocumento?.id) {
      case 'contrato': return 3;
      case 'amonestacion': return 3;
      case 'despido': return 3;
      case 'finiquito': return 3;
      case 'vacaciones': return 3;
      case 'recomendacion': return 3;
      case 'constancia_salarial': return 3;
      default: return 2;
    }
  };

  const getProgreso = () => {
    const total = getTotalPasos();
    return (paso / total) * 100;
  };

  const handleGenerarDocumento = async () => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('generated_by', user.username);
      formDataToSend.append('trabajador_id', trabajadorSeleccionado.id);
      formDataToSend.append('tipo_documento', tipoDocumento.id);
      formDataToSend.append('form_data', JSON.stringify(formData));

      const response = await axios.post(`${API}/documentos/generar`, formDataToSend);
      
      toast.success('Documento generado exitosamente');
      setPdfUrl(response.data.documento.path);
      setPreviewDialog(true);
      
      // Recargar lista de documentos
      loadDocumentosGenerados();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al generar documento';
      toast.error(errorMsg);
    }
  };

  const renderFormularioEspecifico = () => {
    if (!tipoDocumento || !trabajadorSeleccionado) return null;

    // Por ahora solo contrato, los demás en próxima iteración
    if (tipoDocumento.id === 'contrato') {
      return (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Generar Contrato de Trabajo</h3>
            <p className="text-sm text-gray-500 mt-2">
              Se utilizarán los datos actuales del trabajador
            </p>
          </div>
          
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-3">
                <FileText className="w-5 h-5 text-blue-600 mt-1" />
                <div>
                  <p className="font-medium text-blue-900">Información del Trabajador</p>
                  <p className="text-sm text-blue-700 mt-1">
                    <strong>{trabajadorSeleccionado.nombre} {trabajadorSeleccionado.primer_apellido}</strong>
                  </p>
                  <p className="text-sm text-blue-600">
                    Cédula: {trabajadorSeleccionado.numero_identificacion}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={() => setPaso(1)}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Atrás
            </Button>
            <Button onClick={handleGenerarDocumento} className="bg-emerald-600 hover:bg-emerald-700">
              Generar Contrato
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      );
    }

    // Otros tipos - placeholder
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Formulario en desarrollo...</p>
        <Button variant="outline" onClick={() => setPaso(1)} className="mt-4">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
      </div>
    );
  };

  return (
    <RRHHLayout user={user} onLogout={onLogout}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        <Tabs defaultValue="generar" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="generar" className="text-base">
              <FileText className="w-4 h-4 mr-2" />
              Generar Documento
            </TabsTrigger>
            <TabsTrigger value="lista" className="text-base">
              <Eye className="w-4 h-4 mr-2" />
              Documentos Generados ({documentosGenerados.length})
            </TabsTrigger>
          </TabsList>

          {/* TAB: Generar Documento */}
          <TabsContent value="generar">
            {/* Header con progreso */}
            {paso > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Paso {paso} de {getTotalPasos()}
                  </span>
                  <span className="text-sm text-gray-500">
                    {Math.round(getProgreso())}% completado
                  </span>
                </div>
                <Progress value={getProgreso()} className="h-2" />
              </div>
            )}

            {/* Paso 0: Seleccionar Tipo de Documento */}
            {paso === 0 && (
              <div>
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900">Generador de Documentos Legales</h2>
                  <p className="text-gray-600 mt-2">Seleccione el tipo de documento que desea crear</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {TIPOS_DOCUMENTOS.map((tipo) => {
                    const Icon = tipo.icon;
                    return (
                      <Card 
                        key={tipo.id} 
                        className="cursor-pointer hover:shadow-lg transition-all hover:border-emerald-500 border-2"
                        onClick={() => handleSelectTipoDocumento(tipo)}
                      >
                        <CardContent className="pt-6">
                          <div className="flex items-start space-x-4">
                            <div className="p-3 bg-emerald-100 rounded-lg">
                              <Icon className="w-6 h-6 text-emerald-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900">{tipo.nombre}</h3>
                              <p className="text-sm text-gray-500 mt-1">{tipo.descripcion}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Paso 1: Seleccionar Trabajador */}
            {paso === 1 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ChevronLeft className="w-5 h-5 mr-2 cursor-pointer" onClick={() => setPaso(0)} />
                    Seleccionar Trabajador
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label>Documento: {tipoDocumento.nombre}</Label>
                      <Badge className="ml-2" variant="secondary">{tipoDocumento.id}</Badge>
                    </div>

                    <div>
                      <Label>Buscar y Seleccionar Trabajador *</Label>
                      <Select onValueChange={handleSelectTrabajador}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccione un trabajador" />
                        </SelectTrigger>
                        <SelectContent>
                          {trabajadores.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.nombre} {t.primer_apellido} {t.segundo_apellido} - {t.numero_identificacion}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {loadingTrabajadores && (
                      <p className="text-sm text-gray-500">Cargando trabajadores...</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Paso 2+: Formularios Específicos */}
            {paso >= 2 && (
              <Card>
                <CardContent className="pt-6">
                  {renderFormularioEspecifico()}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* TAB: Lista de Documentos */}
          <TabsContent value="lista">
            <Card>
              <CardHeader>
                <CardTitle>Documentos Generados</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Filtros */}
                <div className="flex flex-col space-y-4 mb-6">
                  {/* Primera fila: Búsqueda */}
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Buscar por código, trabajador o cédula..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  {/* Segunda fila: Filtros por tipo y trabajador */}
                  <div className="flex space-x-4">
                    <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                      <SelectTrigger className="w-64">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Filtrar por tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos los tipos</SelectItem>
                        <SelectItem value="contrato">Contratos</SelectItem>
                        <SelectItem value="amonestacion">Amonestaciones</SelectItem>
                        <SelectItem value="despido">Despidos</SelectItem>
                        <SelectItem value="finiquito">Finiquitos</SelectItem>
                        <SelectItem value="vacaciones">Vacaciones</SelectItem>
                        <SelectItem value="recomendacion">Recomendaciones</SelectItem>
                        <SelectItem value="constancia_salarial">Constancias Salariales</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={filtroTrabajador} onValueChange={setFiltroTrabajador}>
                      <SelectTrigger className="flex-1">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Filtrar por trabajador" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos los trabajadores</SelectItem>
                        {trabajadores.map(trabajador => (
                          <SelectItem key={trabajador.id} value={trabajador.id}>
                            {trabajador.nombre} {trabajador.primer_apellido} {trabajador.segundo_apellido || ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Botón para limpiar filtros */}
                    {(filtroTipo !== 'todos' || filtroTrabajador !== 'todos' || busqueda) && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setFiltroTipo('todos');
                          setFiltroTrabajador('todos');
                          setBusqueda('');
                        }}
                      >
                        Limpiar Filtros
                      </Button>
                    )}
                  </div>

                  {/* Contador de resultados */}
                  <div className="text-sm text-gray-600">
                    Mostrando <strong>{getDocumentosFiltrados().length}</strong> de <strong>{documentosGenerados.length}</strong> documentos
                  </div>
                </div>

                {/* Tabla */}
                {loadingDocumentos ? (
                  <p className="text-center py-8 text-gray-500">Cargando documentos...</p>
                ) : getDocumentosFiltrados().length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No hay documentos generados</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Código
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tipo Documento
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Trabajador
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Usuario Generador
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Fecha
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {getDocumentosFiltrados().map((doc) => (
                          <tr key={doc.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap">
                              <Badge variant="outline" className="font-mono text-xs">
                                {doc.codigo}
                              </Badge>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className="text-sm font-medium text-gray-900">
                                {doc.tipo_documento_nombre}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{doc.trabajador_nombre}</p>
                                <p className="text-xs text-gray-500">{doc.trabajador_cedula}</p>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                              {doc.generated_by}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(doc.created_at).toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              <div className="flex space-x-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setDocumentoPreview(doc);
                                    setPreviewTableDialog(true);
                                  }}
                                  title="Previsualizar documento"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = `${BACKEND_URL}${doc.path}`;
                                    link.download = doc.filename;
                                    link.click();
                                  }}
                                  title="Descargar documento"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <CheckCircle2 className="w-5 h-5 text-green-600 mr-2" />
                Documento Generado Exitosamente
              </DialogTitle>
              <DialogDescription>
                El documento ha sido generado y guardado en el expediente del trabajador.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="flex space-x-3">
                <Button 
                  variant="outline"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = `${BACKEND_URL}${pdfUrl}`;
                    link.download = pdfUrl?.split('/').pop();
                    link.click();
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar PDF
                </Button>
                <Button
                  onClick={() => {
                    setPreviewDialog(false);
                    resetearFormulario();
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Crear Otro Documento
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog para previsualizar documentos de la tabla */}
        <Dialog open={previewTableDialog} onOpenChange={setPreviewTableDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Eye className="w-5 h-5 text-emerald-600 mr-2" />
                Previsualización de Documento
              </DialogTitle>
              <DialogDescription>
                {documentoPreview && (
                  <div className="space-y-1">
                    <p><strong>Tipo:</strong> {documentoPreview.tipo_documento_nombre}</p>
                    <p><strong>Trabajador:</strong> {documentoPreview.trabajador_nombre}</p>
                    <p><strong>Código:</strong> {documentoPreview.codigo}</p>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              {documentoPreview && (
                <iframe
                  src={`${BACKEND_URL}${documentoPreview.path}`}
                  className="w-full h-[600px] border rounded-lg"
                  title="Vista previa del documento"
                />
              )}
            </div>

            <div className="flex justify-between pt-4">
              <Button
                variant="outline"
                onClick={() => setPreviewTableDialog(false)}
              >
                Cerrar
              </Button>
              <Button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `${BACKEND_URL}${documentoPreview.path}`;
                  link.download = documentoPreview.filename;
                  link.click();
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Descargar PDF
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </RRHHLayout>
  );
}
